import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import moment, { Moment } from "moment-timezone";

import { RootState } from "../../appRedux/store";
import { CustomNotification } from "../../util/CustomNotification";
import { ErrorMessageConstants } from "../../constants/MessageConstants/ErrorMessageConstants";
import useTablePagination from "../../hooks/useTablePagination";
import * as advanceBookingRequestService from "../../services/advanceBookingRequestService";

import type { FormattedABRRequest, SummaryDetailsInterface } from "../../shared/interface/advanceBookingRequest";
import useValue from "../../hooks/useValue";
import { TableHelper } from "../../util/TableHelper";

export const useAdvanceBookingRequest = () => {
  /* STATE */
  const [abrList, setABRList] = useState<FormattedABRRequest[]>([]);
  const [selectedDate, setSelectedDate] = useState(moment().format("YYYY-MM-DD"));
  const [searchedText, setSearchedText] = useState("");
  const [filteredTableData, setFilteredTableData] = useState<FormattedABRRequest[] | null>(null);
  const [summaryData, setSummaryData] = useState<SummaryDetailsInterface | null>(null);
  const [selectedRecord, setSelectedRecord] = useState<FormattedABRRequest | null>(null);

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isPaymentModalVisible, setIsPaymentModalVisible] = useState(false);
  const [isCancelRequestModalVisible, setIsCancelRequestModalVisible] = useState(false);
  const [isRetrievalRequestModalVisible, setIsRetrievalRequestModalVisible] = useState(false);

  const { pagination, handleChangePagination, handleChangeTotalSize } = useTablePagination(0);
  const { masterFilters } = useSelector((state: RootState) => state.common);

  const { value: loading, handleEnableValue: startLoading, handleDisableValue: stopLoading } = useValue(false);
  const { value: saving, handleEnableValue: startSaving, handleDisableValue: stopSaving } = useValue(false);
  const { value: paymentSummaryLoading, handleEnableValue: startPaymentLoading, handleDisableValue: stopPaymentLoading } = useValue(true);
  const { value: isPayAndParkModalVisible, handleEnableValue: handleEnablePayAndParkModal, handleDisableValue: handleDisablePayAndParkModal } = useValue(false);

  /* HANDLERS */
  const handleDateChange = (date: Moment | null) => {
    setSelectedDate(date ? date.format("YYYY-MM-DD") : "");
  };

  const handleChangeSearch = (value: string) => {
    setSearchedText(value.trimStart());
  };

  const handleFilterTableData = (text: string) => {
    const keys = ["booking_id", "username", "vehicle_type_name", "mobile_number", "parking_start_date", "parking_end_date", "parking_in_time", "parking_out_time"];
    const filtered = TableHelper.getFilterDataSource(abrList, keys, text) as FormattedABRRequest[];
    setFilteredTableData(filtered);
  };

  const fetchABRList = async () => {
    try {
      if (!selectedDate) {
        return CustomNotification.showErrorMessage("Please select a date.");
      }

      const { customerId, locationId } = masterFilters || {};
      if (!customerId || !locationId) {
        return CustomNotification.showErrorMessage(ErrorMessageConstants.SELECT_CUSTOMER_LOCATION_FILTER);
      }

      startLoading();

      const params = {
        fromDate: moment(selectedDate).startOf("day").format("YYYY-MM-DD HH:mm"),
        toDate: moment(selectedDate).endOf("day").format("YYYY-MM-DD HH:mm"),
        customerId,
        locationId
      };

      const response = await advanceBookingRequestService.getABRList(params);

      if (response?.data) {
        const formatted = response.data.map((item: any, index: number) => ({
          ...item,
          id: index + 1,
          advance_booking_request_id: item.id,
          username: item.mobileUser?.fullName ?? "",
          mobile_number: item.mobileUser?.mobile_number ?? "",
          vehicle_type_name: item.vehicleType?.vehicle_type_name ?? "",
          parking_amount: item.amount.parking_amount,
          valet_parking_amount: item.amount.valet_parking_amount,
          penalty_amount: item.amount.penalty_amount,
          overdue_amount: item.amount.overdue_amount,
          payable_amount: item.amount.payable_amount,
          key_fob_token: item?.keyFobAllocation?.keyFob?.key_fob_token ?? null
        }));

        setABRList(formatted as FormattedABRRequest[]);
        setFilteredTableData(formatted as unknown as FormattedABRRequest[]);
        if (searchedText) handleFilterTableData(searchedText);
      }
    } catch (error) {
      CustomNotification.showErrorMessage(error);
    } finally {
      stopLoading();
    }
  };

  const handleRefreshList = () => fetchABRList();

  const handleOnClose = () => {
    setIsModalVisible(false);
    setSelectedRecord(null);
  };

  const handleCancelRequestConfirm = async () => {
    if (!selectedRecord?.advance_booking_request_id) return;

    startSaving();
    try {
      const response = await advanceBookingRequestService.cancelAbrRequestAPI(selectedRecord.advance_booking_request_id);
      CustomNotification.showSuccessMessage(response?.message);
      setIsCancelRequestModalVisible(false);
      setSelectedRecord(null);
      handleRefreshList();
    } catch (error) {
      CustomNotification.showErrorMessage(error);
    } finally {
      stopSaving();
    }
  };

  const handleRetrievalRequestConfirm = async () => {
    if (!selectedRecord?.advance_booking_request_id) return;

    startSaving();
    try {
      const response = await advanceBookingRequestService.raiseParkingRetrievalRequestAPI(selectedRecord.advance_booking_request_id);
      CustomNotification.showSuccessMessage(response?.message);
      setIsRetrievalRequestModalVisible(false);
      setSelectedRecord(null);
      handleRefreshList();
    } catch (error) {
      CustomNotification.showErrorMessage(error);
    } finally {
      stopSaving();
    }
  };

  const fetchSummaryData = async (id: number) => {
    if (!id) return;

    startPaymentLoading();
    try {
      const response = await advanceBookingRequestService.calculateOverdueAmountForAdvanceBookingAPI(id);
      setSummaryData(response?.data);
    } catch (error) {
      handlePaymentSummeryModalClose();
      CustomNotification.showErrorMessage(error);
    } finally {
      stopPaymentLoading();
    }
  };

  const handleModalOpen = (setter: (val: boolean) => void, record: FormattedABRRequest) => {
    setSelectedRecord(record);
    setter(true);
  };

  const handleCancelRequestClose = () => {
    setIsCancelRequestModalVisible(false);
    setSelectedRecord(null);
  };

  const handleRetrievalRequestClose = () => {
    setIsRetrievalRequestModalVisible(false);
    setSelectedRecord(null);
  };

  const handlePaymentSummeryModalOpen = (record: FormattedABRRequest) => {
    handleModalOpen(setIsPaymentModalVisible, record);
    fetchSummaryData(record.advance_booking_request_id);
  };

  const handlePaymentSummeryModalClose = () => {
    setSelectedRecord(null);
    setIsPaymentModalVisible(false);
  };

  const handleAddABRRequest = (record: FormattedABRRequest) => {
    if (!masterFilters?.customerId || !masterFilters?.locationId) {
      CustomNotification.showErrorMessage(ErrorMessageConstants.SELECT_CUSTOMER_LOCATION_FILTER);
      return;
    }
    handleModalOpen(setIsModalVisible, record);
  };

  /* EFFECT */
  useEffect(() => {
    handleFilterTableData(searchedText);
  }, [searchedText]);

  useEffect(() => {
    if (masterFilters?.customerId && masterFilters?.locationId) {
      fetchABRList();
    }
  }, [masterFilters?.customerId, masterFilters?.locationId, selectedDate]);

  return {
    abrList,
    loading,
    pagination,
    handleChangePagination,
    setSelectedDate,
    selectedDate,
    fetchABRList,
    handleDateChange,
    handleAddABRRequest,
    handleRefreshList,
    handleOnClose,
    isModalVisible,
    selectedRecord,
    handleCancelRequestConfirm,
    handleCancelRequestClose,
    handleRetrievalRequestClose,
    handleRetrievalRequestConfirm,
    isCancelRequestModalVisible,
    isRetrievalRequestModalVisible,
    handleCancelABRRequest: (record: FormattedABRRequest) => handleModalOpen(setIsCancelRequestModalVisible, record),
    handleRaiseRetrievalRequest: (record: FormattedABRRequest) => handleModalOpen(setIsRetrievalRequestModalVisible, record),
    isPaymentModalVisible,
    handlePaymentSummeryModalClose,
    handlePaymentSummeryModalOpen,
    saving,
    filteredTableData,
    handleChangeSearch,
    searchedText,
    paymentSummaryLoading,
    summaryData,
    isPayAndParkModalVisible,
    handleEnablePayAndParkModal,
    handleDisablePayAndParkModal
  };
};
